import React, {useEffect} from 'react'
import { useTranslation } from 'react-i18next'
import {Form} from 'antd'
import {observer} from 'mobx-react'
import {useInjection} from 'dna-react-ioc'
import {useSearchParams} from 'react-router-dom'
import {Buffer} from 'buffer'

import {withTitle} from '@/router/utils'
import {LoadingState} from '@/types'
import {BackToLoginLink, Button, Card, Input} from '@/components'
import {TResetPasswordStore} from '@/pages/ResetPassword'
import {EMAIL_TO_RESET, SUBMIT_RESET} from '@/constants/playwright-ids'
import styles from './styles.scss'

export const ResetPasswordStoreSymbol = Symbol('ResetPasswordStore')

const ResetPassword = observer(() => {
    const { t } = useTranslation()
    const [form] = Form.useForm()
    const store = useInjection<TResetPasswordStore>(ResetPasswordStoreSymbol)

    const [queryParams] = useSearchParams()
    const paramsValue = queryParams.get('t') || ''

    useEffect(() => {
        let email = null

        if (!paramsValue) {
            return
        }

        try {
            email = paramsValue ? Buffer.from(paramsValue, 'base64').toString() : null
            store.sendInstructions({email: email?.toLowerCase()})
        } catch (error) {
            const errorMessage = t('ResetPassword.errorOnEmail', { message: error.message })
            store.navigateToPasswordFailure(errorMessage)
            return
        }
    }, [paramsValue])

    const onFinish = values => {
        values.email = values.email?.toLowerCase()
        store.sendInstructions(values)
    }

    const formId = 'reset-password'

    return paramsValue ? null : (
        <>
            <div className={styles.resetCard}>
                <BackToLoginLink/>
                <Card title={t('ResetPassword.title')}>
                    <div>{t('ResetPassword.text')}</div>
                    <br />
                    <Form
                        form={form}
                        id={formId}
                        layout='vertical'
                        requiredMark={false}
                        onFinish={onFinish}
                    >
                        <Form.Item
                            label={t('ResetPassword.email')}
                            name='email'
                            rules={[
                                {required: true, message: t('ResetPassword.emailRequired')},
                                {
                                    type: 'email',
                                    message: t('ResetPassword.emailInvalid')
                                }
                            ]}
                        >
                            <Input placeholder={t('ResetPassword.email')} data-test-id={EMAIL_TO_RESET} />
                        </Form.Item>
                        <Form.Item key={'submit'}>
                            <Button
                                form={formId}
                                type={'primary'}
                                htmlType={'submit'}
                                className={styles.submit}
                                loading={store.resetPasswordLoadingState === LoadingState.LOADING}
                                data-test-id={SUBMIT_RESET}
                            >
                                {t('ResetPassword.submit')}
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            </div>
        </>
    )
})

export default withTitle(ResetPassword)
